<template>
  <v-card
    class="rounded-xl textColor--text"
    rounded
    width="320"
    :ripple="true"
    :href="galleryUrl"
  >
    <v-card-title
      style="
        display: flex;
        align-items: center;
        overflow: hidden;
        flex-wrap: nowrap;
      "
    >
      <ConnectIcon
        v-if="showLevelIcon"
        size="24"
        style="margin-right: 4px; flex-shrink: 0"
      />
      <v-tooltip top>
        <template v-slot:activator="{ on, attrs }">
          <div
            style="
              text-overflow: ellipsis;
              white-space: nowrap;
              overflow: hidden;
            "
            v-bind="attrs"
            v-on="on"
          >
            {{ eventName }}
          </div>
        </template>
        <span>{{ eventName }}</span>
      </v-tooltip>
    </v-card-title>
    <v-card-subtitle>{{ eventDateDisplayString }}</v-card-subtitle>
    <v-card-text>
      <div style="display: flex; align-items: center; justify-content: start">
        <Avatar
          style="
            flex-grow: 0;
            flex-shrink: 1;
            margin-left: 0;
            margin-right: 8px;
          "
          :name="userName"
          :image-url="userImageSrc"
          variant="xxs"
        />
        <div style="display: flex; flex-direction: row; align-items: center">
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <Subtitle
                :font-size="0.9"
                :text="userName"
                :font-weight="700"
                :line-height="16"
                class="mb-0 two-line-ellipsis"
                v-bind="attrs"
                v-on="on"
              />
            </template>
            <span>{{ userName }}</span>
          </v-tooltip>
        </div>
      </div>
      <div class="image-grid" :class="additionalGridClasses">
        <v-img
          v-for="image in images"
          :key="image.src"
          :src="image.src"
          aspect-ratio="1"
          class="rounded-lg"
        />
      </div>
      <div class="image-count">
        {{ imageCountString }}
        <RelativeTime hide-icon :date-time="postDate" style="display: inline" />
      </div>
    </v-card-text>
  </v-card>
</template>

<script lang="ts">
import { computed, defineComponent } from "vue"
import { formatDateToDDMonthYYYY } from "app/util/date.helper"
import Avatar from "components/UI/Avatar.vue"
import RelativeTime from "components/UI/RelativeTime.vue"
import Subtitle from "components/UI/Subtitle.vue"
import { mdiGoogleCirclesCommunities } from "@mdi/js"
import ConnectIcon from "components/icons/ConnectIcon.vue"

export interface GalleryImage {
  src: string
}

export default defineComponent({
  name: "GalleryDashboardCardUI",
  components: { ConnectIcon, Subtitle, RelativeTime, Avatar },
  props: {
    eventName: { type: String, required: true },
    eventDate: { type: Date, required: true },
    images: { type: Array as () => GalleryImage[], required: true },
    allImagesCount: { type: Number, required: true },
    userImageSrc: { type: String, required: false },
    userName: { type: String, required: true },
    postDate: { type: Date, required: true },
    showLevelIcon: { type: Boolean, required: false, default: false },
    galleryUrl: { type: String, required: false },
  },
  setup(props) {
    const eventDateDisplayString = computed(
      () => `am ${formatDateToDDMonthYYYY(props.eventDate)}`
    )

    const imageCountString = computed(() => {
      if (props.allImagesCount === 1) {
        return `Ein Foto`
      }
      return `${props.allImagesCount} Fotos`
    })

    const additionalGridClasses = computed(() => {
      if (props.images.length === 1) {
        return "one-image"
      }
      if (props.images.length === 2) {
        return "two-images"
      }
      if (props.images.length === 3) {
        return "three-images"
      }
      return ""
    })

    return {
      eventDateDisplayString,
      imageCountString,
      additionalGridClasses,
      mdiGoogleCirclesCommunities,
    }
  },
})
</script>

<style scoped>
.image-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  gap: 8px;
}

.image-count {
  display: flex;
  justify-content: space-between;
  margin-top: 4px;
  color: rgb(147, 147, 147);
  font-weight: 400;
  font-family: system-ui !important;
  font-size: 0.8rem;
}

/*Used in [additionalGridClasses]*/
/*noinspection CssUnusedSymbol*/
.one-image {
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;
}

/*Used in [additionalGridClasses]*/
/*noinspection CssUnusedSymbol*/
.two-images {
  grid-template-columns: 1fr;
  grid-template-rows: 140px 140px;
}

/*Used in [additionalGridClasses]*/
/*noinspection CssUnusedSymbol*/

.three-images > :nth-child(3) {
  grid-column: 1 / -1;
}

.three-images {
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 140px 140px;
}
.two-line-ellipsis {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
